import {useContext, useEffect, useState} from "react";
import axios from "axios";
import Block from "../block";
import Spinner from "../../components/spinner";
import {WebsiteContext} from "../../index";
import {formatDateToFriendlyDate} from "./account-date-utils";
import {onlyActiveSecurityHolds} from "./payment-visibility";
import AccountPaymentMethodForm from "./account-payment-method-form";
import {formatPaymentMethodBrand} from "../../components/payment-method-utils";
import SavedPaymentMethodSummary, {PaymentCardIcon} from "../../components/saved-payment-method-summary";

const formatCardLabel = (method) => {
    const brand = formatPaymentMethodBrand(method?.brand);
    return `${brand} ending ${method?.lastFour || "••••"}`;
};

export const getRemoveCardConfirmation = (method) => (
    `Remove ${formatCardLabel(method)}? You'll need to add it again before you can use it for a future payment.`
);

const formatMoney = (amount, currency = "AUD", country) => {
    const locale = country ? `en-${country}` : currency === "NZD" ? "en-NZ" : "en-AU";
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(amount || 0) / 100);
};

const getHoldProviderExpiry = (hold) => (
    hold?.providerCaptureBeforeUtc
    || hold?.providerCaptureBefore
    || hold?.expiresAtUtc
    || hold?.expiresAt
);

export default function AccountPayments(props) {
    const context = useContext(WebsiteContext);
    const customerId = props?.profile?.customer?.id;
    const profileMethods = props?.profile?.customer?.paymentMethods || [];
    const profileHolds = onlyActiveSecurityHolds(props?.profile?.customer?.securityHolds);
    const [paymentMethods, setPaymentMethods] = useState(profileMethods);
    const [holds, setHolds] = useState(profileHolds);
    const [loading, setLoading] = useState(true);
    const [showLoader, setShowLoader] = useState(false);
    const [confirmingId, setConfirmingId] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [message, setMessage] = useState(null);
    const [showAddCard, setShowAddCard] = useState(false);

    const hasPaymentMethods = paymentMethods.length > 0;
    const hasHolds = holds.length > 0;

    useEffect(() => {
        document.title = props?.title || "Payments";
    }, [props?.title]);

    useEffect(() => {
        if (!customerId) {
            setLoading(false);
            return;
        }

        const cancelSource = axios.CancelToken.source();
        const timer = setTimeout(() => setShowLoader(true), 400);
        setLoading(true);

        Promise.all([
            axios.get(`/api/v2/customers/${customerId}/payment-methods`, {cancelToken: cancelSource.token}),
            axios.get(`/api/v2/customers/${customerId}/security-holds`, {cancelToken: cancelSource.token})
        ])
            .then(([methodsResponse, holdsResponse]) => {
                setPaymentMethods(Array.isArray(methodsResponse.data) ? methodsResponse.data : []);
                setHolds(onlyActiveSecurityHolds(holdsResponse.data));
            })
            .catch((error) => {
                if (!axios.isCancel(error)) {
                    console.error("Error fetching payment details:", error);
                    setMessage({type: "error", text: "We couldn't load your payment details. Please try again."});
                }
            })
            .finally(() => {
                clearTimeout(timer);
                setLoading(false);
                setShowLoader(false);
            });

        return () => {
            clearTimeout(timer);
            cancelSource.cancel();
        };
    }, [customerId]);

    const methodBlockedByHold = (method) => holds.some((hold) => hold?.paymentMethod?.id === method.id);

    const addPaymentMethod = (method) => {
        setPaymentMethods((current) => [
            ...current.filter((item) => item.id !== method?.id),
            method
        ]);
        setShowAddCard(false);
        setMessage({type: "success", text: "Card saved."});
    };

    const removePaymentMethod = (method) => {
        if (!customerId || !method?.id || methodBlockedByHold(method)) return;

        setConfirmingId(null);
        setRemovingId(method.id);
        setMessage(null);
        axios.delete(`/api/v2/customers/${customerId}/payment-methods/${method.id}`)
            .then(() => {
                setPaymentMethods((current) => current.filter((item) => item.id !== method.id));
                setMessage({type: "success", text: "Card removed."});
            })
            .catch((error) => {
                const status = error?.response?.status;
                const text = status === 409
                    ? "This card can't be removed while it is being used for a card hold."
                    : "We couldn't remove that card. Please try again.";
                setMessage({type: "error", text});
            })
            .finally(() => setRemovingId(null));
    };

    return (
        <Block {...props}>
            <h2>{props?.title}</h2>

            {loading
                ? showLoader && <Spinner/>
                : (
                    <div className="mt-5 space-y-8">
                        {message &&
                            <div role={message.type === "error" ? "alert" : "status"}
                                 aria-live={message.type === "error" ? "assertive" : "polite"}
                                 className={message.type === "success"
                                     ? "rounded-theme bg-green-50 text-green-800 px-4 py-3"
                                     : "rounded-theme bg-red-50 text-red-800 px-4 py-3"}>
                                {message.text}
                            </div>
                        }

                        <section aria-labelledby="saved-cards-heading">
                            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                <div>
                                    <h3 id="saved-cards-heading" className="!text-base !leading-snug font-bold !mb-0">Saved Cards</h3>
                                    <p className="mt-1 !mb-0 text-sm text-gray-700">
                                        Choose a saved card when a payment or card hold is required.
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-gray-600">
                                        {paymentMethods.length} {paymentMethods.length === 1 ? "card" : "cards"}
                                    </span>
                                    {hasPaymentMethods && !showAddCard ? (
                                        <button type="button" className="text-link font-semibold hover:opacity-80" onClick={() => setShowAddCard(true)}>
                                            Add Card
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                            {showAddCard ? (
                                <div className="section-grey rounded-theme container-padding-half mb-4">
                                    <AccountPaymentMethodForm
                                        customerId={customerId}
                                        onSaved={addPaymentMethod}
                                        onCancel={() => setShowAddCard(false)}
                                    />
                                </div>
                            ) : null}
                            {hasPaymentMethods ? <div className="space-y-3">
                                {paymentMethods.map((method) => {
                                    const blocked = methodBlockedByHold(method);

                                    return (
                                        <div key={method.id}
                                             className="section-grey rounded-theme container-padding-half flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                            <SavedPaymentMethodSummary
                                                method={method}
                                                description={blocked
                                                    ? "In use by an active hold and can't be removed yet."
                                                    : null}
                                            />
                                            {confirmingId === method.id ? (
                                                <div role="group"
                                                     aria-label={`Confirm removal of ${formatCardLabel(method)}`}
                                                     className="flex flex-col items-start gap-2 sm:items-end">
                                                    <p className="text-sm text-gray-700 sm:text-right max-w-sm mb-0">
                                                        {getRemoveCardConfirmation(method)}
                                                    </p>
                                                    <div className="flex flex-wrap items-center gap-4">
                                                        <button type="button"
                                                                onClick={() => setConfirmingId(null)}
                                                                className="text-link text-sm font-semibold hover:opacity-80">
                                                            Keep Card
                                                        </button>
                                                        <button type="button"
                                                                onClick={() => removePaymentMethod(method)}
                                                                className="text-sm font-semibold text-red-600 hover:opacity-80">
                                                            Remove This Card
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    disabled={blocked || removingId === method.id}
                                                    onClick={() => setConfirmingId(method.id)}
                                                    className="text-link w-fit text-sm font-semibold hover:opacity-80 disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline">
                                                    {removingId === method.id ? "Removing…" : "Remove Card"}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div> : (
                                !showAddCard ? (
                                    <div className="flex items-start gap-4">
                                        <PaymentCardIcon className="mt-0.5"/>
                                        <div>
                                            <h4 className="!text-base !leading-snug font-bold mb-1">No Saved Payment Details</h4>
                                            <p className="text-base text-gray-700 mb-1">
                                                Cards you choose to save during registration or payment will appear here.
                                            </p>
                                            <p className="text-sm text-gray-600 mb-0">
                                                Card details are stored securely by our payment provider. Any temporary holds will also be shown here while they are active.
                                            </p>
                                            <button type="button" className="text-link mt-3 font-semibold hover:opacity-80" onClick={() => setShowAddCard(true)}>
                                                Add Card
                                            </button>
                                        </div>
                                    </div>
                                ) : null
                            )}
                        </section>

                        {hasHolds ? <section aria-labelledby="temporary-card-holds-heading" className="border-t border-gray-200 pt-7">
                            <h3 id="temporary-card-holds-heading" className="!text-base !leading-snug font-bold !mb-0">Temporary Card Holds</h3>
                            <p className="mt-1 mb-4 text-sm text-gray-700">
                                A hold reserves an amount on your card without taking payment. It may be resolved earlier; otherwise it expires by the time shown.
                            </p>
                            <div className="space-y-3">
                                {holds.map((hold) => {
                                    return (
                                        <div key={hold.id} className="section-grey rounded-theme container-padding-half">
                                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                                <div>
                                                    <div className="font-bold">
                                                        {formatMoney(
                                                            hold.amount,
                                                            hold.currency || context.currency,
                                                            context.country
                                                        )}
                                                        {" "}Temporary Hold
                                                    </div>
                                                    {hold.paymentMethod &&
                                                        <div className="text-sm text-gray-700">
                                                            {formatCardLabel(hold.paymentMethod)}
                                                        </div>
                                                    }
                                                </div>
                                                <span className="text-xs section-black font-bold rounded-full px-3 py-1 uppercase w-fit">
                                                    Active
                                                </span>
                                            </div>
                                            <div className="text-sm text-gray-700 mt-2">
                                                Hold expires by {formatDateToFriendlyDate(getHoldProviderExpiry(hold))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section> : null}

                    </div>
                )
            }
        </Block>
    );
}
